import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, MessageCircle } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { api } from "../lib/api";
import { WHATSAPP_COMMUNITY_URL } from "../lib/constants";

export default function Footer() {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);

  const subscribe = async (event) => {
    event.preventDefault();
    if (!email) return;
    try {
      await api.post("/newsletter", { email, language: lang });
      setStatus("ok");
      setEmail("");
    } catch {
      setStatus("err");
    }
  };

  return (
    <footer className="bg-navy text-alabaster relative overflow-hidden" data-testid="site-footer">
      <div
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 20%, #D7A33D 0%, transparent 45%), radial-gradient(circle at 85% 80%, #D71920 0%, transparent 45%)",
        }}
      />

      <div className="relative border-b border-alabaster/10" data-testid="footer-community-band">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex-1">
            <p className="text-[10px] tracking-[0.3em] uppercase text-gold mb-2">— {t("footer.community_title")}</p>
            <p className="font-serif text-2xl lg:text-3xl leading-tight max-w-2xl">{t("footer.community_sub")}</p>
          </div>
          <a
            href={WHATSAPP_COMMUNITY_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="footer-community-cta"
            className="inline-flex items-center gap-3 bg-[#25D366] text-white px-5 py-3 rounded-full text-sm font-medium hover:bg-[#1FB855] transition whitespace-nowrap"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.693.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            {t("footer.community_cta")}
          </a>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 relative">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-4">
              <img
                src="/logo.png"
                alt="JNIC"
                width="64"
                height="64"
                loading="lazy"
                decoding="async"
                className="w-16 h-16 rounded-full object-cover ring-1 ring-gold/40 shrink-0"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "/jnic-premium-logo.png";
                }}
              />
              <div>
                <p className="font-serif text-2xl leading-none">JN Immigration</p>
                <p className="text-[9px] tracking-[.28em] uppercase text-redcanada mt-2">Consulting</p>
              </div>
            </div>
            <h3 className="font-serif text-4xl leading-tight mt-7">{t("footer.tagline")}</h3>
            <p className="text-alabaster/60 text-sm mt-6 max-w-md leading-relaxed">
              {lang === "fr"
                ? "Du monde francophone au Canada. Accompagnement bilingue, à distance et honnête."
                : "From the French-speaking world to Canada. Bilingual, remote and honest guidance."}
            </p>
            <div className="mt-6 flex flex-col sm:flex-row sm:flex-wrap gap-3 text-sm">
              <a href="mailto:info@jnic.org" className="inline-flex items-center gap-2 text-alabaster/70 hover:text-gold transition">
                <Mail size={15} /> info@jnic.org
              </a>
              <a href="https://wa.me/237694056334" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-alabaster/70 hover:text-[#25D366] transition">
                <MessageCircle size={15} /> +237 694 05 63 34
              </a>
            </div>
            <p className="text-alabaster/45 text-xs mt-5 max-w-xl leading-relaxed">{t("footer.disclaimer")}</p>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs tracking-[0.25em] uppercase text-alabaster/50 mb-4">{t("footer.sitemap")}</p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/services" className="hover:text-redcanada">{t("nav.services")}</Link></li>
              <li><Link to="/tcf" className="hover:text-redcanada">{t("nav.tcf")}</Link></li>
              <li><Link to="/events" className="hover:text-redcanada">{t("nav.events")}</Link></li>
              <li><Link to="/registry" className="hover:text-redcanada">{t("nav.registry")}</Link></li>
              <li><Link to="/about" className="hover:text-redcanada">{t("nav.about")}</Link></li>
              <li><Link to="/contact" className="hover:text-redcanada">{t("nav.contact")}</Link></li>
              <li>
                <Link
                  to="/depot-documents"
                  className="inline-flex items-center gap-1.5 text-xs text-gold/90 hover:text-white transition font-semibold"
                >
                  <span>📥 {lang === "fr" ? "Dépôt sécurisé de documents" : "Secure Document Drop Box"}</span>
                </Link>
              </li>
              <li>
                <a
                  href="/api/download-zip"
                  download="jnic-immigration-consulting-source.zip"
                  className="inline-flex items-center gap-1.5 text-xs text-gold/90 hover:text-gold transition font-medium mt-2"
                >
                  <span>📦 {lang === "fr" ? "Télécharger le code source (ZIP)" : "Download Source Code (ZIP)"}</span>
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="text-xs tracking-[0.25em] uppercase text-alabaster/50 mb-4">{t("footer.newsletter")}</p>
            <form onSubmit={subscribe} className="flex gap-2" data-testid="newsletter-form">
              <label className="relative flex-1">
                <Mail size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-alabaster/45" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@email.com"
                  data-testid="newsletter-email"
                  className="w-full bg-white/5 border border-alabaster/20 rounded-full pl-10 pr-4 py-3 text-sm focus:border-gold focus:outline-none"
                />
              </label>
              <button
                type="submit"
                data-testid="newsletter-submit"
                className="w-12 h-12 rounded-full bg-redcanada text-white grid place-items-center hover:bg-gold hover:text-navy transition"
                aria-label={t("footer.subscribe")}
              >
                <ArrowRight size={17} />
              </button>
            </form>
            {status === "ok" && <p className="text-xs text-gold mt-3" data-testid="newsletter-success">{t("footer.subscribed")}</p>}
            {status === "err" && <p className="text-xs text-redcanada-light mt-3">{t("common.error")}</p>}
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-alabaster/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-alabaster/50">
          <p>{t("footer.copy")}</p>
          <p className="italic">“Empowerment over dependency.”</p>
        </div>
      </div>
    </footer>
  );
}
